/**
 * Narration scroll positioning.
 *
 * Narration anchors the destination block's section 50px below the effective
 * viewport top — the bottom of the sticky top band when one is present,
 * otherwise the top of the player's scroll viewport — so a strip of the
 * preceding card stays visible as a forward-motion cue and the controls
 * cluster lands on screen. The move is animated over a fixed duration and
 * reports when it settles, because playback waits for it.
 *
 * `scrollTop` math on the scroll viewport rather than `scrollIntoView`:
 * native smooth scrolling has no reliable completion signal (`scrollend` is
 * missing on Safari < 18.4, common in corporate SCORM environments) and
 * `scrollIntoView` can scroll a SCORM host page's ancestors.
 */

import {
  COURSE_SCROLL_ROOT_SELECTOR,
  attachManualIntentListeners,
} from './narrationManualIntent';
import { prefersReducedMotion } from './hooks/usePrefersReducedMotion';

/** Strip of the preceding card left visible above the anchored section. */
export const NARRATION_SCROLL_CUE_OFFSET_PX = 50;
export const NARRATION_SCROLL_DURATION_MS = 500;
/** Local coordination only: reset residual learner-scroll intent before positioning. */
export const NARRATION_SCROLL_START_EVENT = 'narration-scroll-start';

/** Marks the sticky top band only; non-sticky top content scrolls away. */
const STICKY_TOP_BAND_SELECTOR = '[data-sticky-top-band]';

/** Below this the animation is pointless — jump and settle. */
const NEGLIGIBLE_SCROLL_DELTA_PX = 1;
// Leave the bottom inside the viewport despite scrollTop rounding at browser zoom.
const BOTTOM_VISIBILITY_INSET_PX = 1;

export interface NarrationScrollHandle {
  /** Stops the animation in place. Idempotent; never reports a settle. */
  cancel: () => void;
}

/**
 * Positions `blockId` for narration. Returns `null` when the block has not
 * mounted yet so callers can retry, and reports settle exactly once when the
 * viewport reaches its target. `onInterrupted` fires only for learner
 * takeover — never for a programmatic `cancel()`.
 */
export type NarrationBlockScroller = (
  blockId: string,
  onSettled: () => void,
  onInterrupted?: () => void,
) => NarrationScrollHandle | null;

const NOOP_HANDLE: NarrationScrollHandle = { cancel: () => undefined };

function findNarrationBlockElement(blockId: string): HTMLElement | null {
  const elements = document.querySelectorAll<HTMLElement>('[data-block-id]');
  for (const element of elements) {
    if (element.dataset.blockId === blockId) return element;
  }
  return null;
}

/** Height of the sticky top band inside `scrollRoot`; 0 when there is none. */
export function measureStickyTopBandHeight(scrollRoot: Element): number {
  const band = scrollRoot.querySelector<HTMLElement>(STICKY_TOP_BAND_SELECTOR);
  return band ? band.getBoundingClientRect().height : 0;
}

/** Scroll viewport top plus any sticky band covering it. */
function measureEffectiveViewportTop(scrollRoot: HTMLElement): number {
  return scrollRoot.getBoundingClientRect().top + measureStickyTopBandHeight(scrollRoot);
}

/** Clamped `scrollTop` that puts `target`'s top at the cue offset. */
export function computeNarrationScrollTop(scrollRoot: HTMLElement, target: Element): number {
  const desired = scrollRoot.scrollTop
    + (target.getBoundingClientRect().top - measureEffectiveViewportTop(scrollRoot))
    - NARRATION_SCROLL_CUE_OFFSET_PX;
  const maxScrollTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
  return Math.min(Math.max(desired, 0), maxScrollTop);
}

function easeOutCubic(progress: number): number {
  return 1 - (1 - progress) ** 3;
}

function scrollNarrationBlock(
  position: 'start' | 'bottom',
  blockId: string,
  onSettled: () => void,
  onInterrupted?: () => void,
): NarrationScrollHandle | null {
  const blockElement = findNarrationBlockElement(blockId);
  if (!blockElement) return null;

  const scrollRoot = blockElement.closest<HTMLElement>(COURSE_SCROLL_ROOT_SELECTOR);
  // No player viewport to move: settle so narration is never stranded, and
  // never fall back to scrollIntoView, which can scroll a host page.
  if (!scrollRoot) {
    onSettled();
    return NOOP_HANDLE;
  }

  scrollRoot.dispatchEvent(new Event(NARRATION_SCROLL_START_EVENT));

  // The section is the visual card. The controls cluster anchors to its top
  // edge, well above the block's own content, so anchoring the block element
  // would leave the cluster off screen.
  const section = blockElement.closest('section') ?? blockElement;
  const from = scrollRoot.scrollTop;
  const bottomTarget = blockElement.querySelector('[data-scroll-completion-anchor]') ?? blockElement;
  const bottomScrollTop = from + bottomTarget.getBoundingClientRect().bottom - scrollRoot.getBoundingClientRect().bottom + BOTTOM_VISIBILITY_INSET_PX;
  const to = position === 'bottom'
    ? Math.min(Math.max(0, bottomScrollTop), Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight))
    : computeNarrationScrollTop(scrollRoot, section);
  const distance = to - from;
  const isNegligible = Math.abs(distance) < NEGLIGIBLE_SCROLL_DELTA_PX;

  if (
    isNegligible
    || prefersReducedMotion()
    || typeof window === 'undefined'
    || typeof window.requestAnimationFrame !== 'function'
  ) {
    if (!isNegligible) scrollRoot.scrollTop = to;
    onSettled();
    return NOOP_HANDLE;
  }

  let frame: number | null = null;
  let startTimestamp: number | null = null;
  let finished = false;
  let detachManualIntent: () => void = () => undefined;

  const stop = (): void => {
    if (finished) return;
    finished = true;
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
    detachManualIntent();
  };

  const step = (timestamp: number): void => {
    frame = null;
    if (finished) return;
    if (startTimestamp === null) startTimestamp = timestamp;
    const progress = Math.min(1, (timestamp - startTimestamp) / NARRATION_SCROLL_DURATION_MS);
    scrollRoot.scrollTop = from + distance * easeOutCubic(progress);
    if (progress >= 1) {
      stop();
      onSettled();
      return;
    }
    frame = window.requestAnimationFrame(step);
  };

  detachManualIntent = attachManualIntentListeners(() => {
    if (finished) return;
    stop();
    onInterrupted?.();
  });
  frame = window.requestAnimationFrame(step);

  return { cancel: stop };
}

export const scrollNarrationBlockIntoView: NarrationBlockScroller = (...args) => scrollNarrationBlock('start', ...args);
export const scrollNarrationBlockBottomIntoView: NarrationBlockScroller = (...args) => scrollNarrationBlock('bottom', ...args);
